import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Pencil, Trash2, FileText, CreditCard, ArrowRightLeft, Check, Clock, AlertTriangle } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const formatCurrency = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);
const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

export default function InvoicesPage() {
  const { isRole } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(null); // 'invoice' | 'payment' | 'transaction' | 'pay_invoice' | 'pay_payment'
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    try {
      const [iRes, pRes, tRes, sRes, stRes] = await Promise.all([
        api.get('/invoices'), api.get('/payments'), api.get('/transactions'), api.get('/suppliers'), api.get('/invoices/stats')
      ]);
      setInvoices(iRes.data);
      setPayments(pRes.data);
      setTransactions(tRes.data);
      setSuppliers(sRes.data);
      setStats(stRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (type) => {
    try {
      const endpoint = type === 'invoice' ? '/invoices' : type === 'payment' ? '/payments' : '/transactions';
      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, form);
      } else {
        await api.post(endpoint, form);
      }
      setShowDialog(null); setEditing(null); setForm({}); load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleMarkPaid = async (type, item) => {
    try {
      const endpoint = type === 'invoice' ? `/invoices/${item.id}` : `/payments/${item.id}`;
      await api.put(endpoint, { status: 'pagata', payment_date: new Date().toISOString().split('T')[0], payment_receipt_url: form.payment_receipt_url || '', receipt_url: form.receipt_url || '' });
      setShowDialog(null); setForm({}); load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Eliminare?')) return;
    const endpoint = type === 'invoice' ? `/invoices/${id}` : type === 'payment' ? `/payments/${id}` : `/transactions/${id}`;
    try { await api.delete(endpoint); load(); } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const statusBadge = (status, dueDate) => {
    if (status === 'pagata') return <Badge className="bg-green-100 text-green-800">Pagata</Badge>;
    const isOverdue = dueDate && dueDate < new Date().toISOString().split('T')[0];
    if (isOverdue) return <Badge className="bg-red-100 text-red-800">Scaduta</Badge>;
    return <Badge className="bg-amber-100 text-amber-800">Da pagare</Badge>;
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-pulse text-slate-400">Caricamento...</div></div>;

  return (
    <div className="space-y-4" data-testid="invoices-page">
      <h1 className="text-2xl font-bold text-slate-900">Fatture e Pagamenti</h1>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-amber-50 border-amber-200"><CardContent className="p-4"><p className="text-xs text-amber-600">Da Pagare</p><p className="text-xl font-bold text-amber-800">{formatCurrency(stats.total_unpaid)}</p></CardContent></Card>
        <Card className="bg-green-50 border-green-200"><CardContent className="p-4"><p className="text-xs text-green-600">Pagato</p><p className="text-xl font-bold text-green-800">{formatCurrency(stats.total_paid)}</p></CardContent></Card>
        <Card className="bg-red-50 border-red-200"><CardContent className="p-4"><p className="text-xs text-red-600">Scadute</p><p className="text-xl font-bold text-red-800">{stats.overdue_count || 0}</p></CardContent></Card>
        <Card className="bg-blue-50 border-blue-200"><CardContent className="p-4"><p className="text-xs text-blue-600">Totale Fatture</p><p className="text-xl font-bold text-blue-800">{stats.total_count || 0}</p></CardContent></Card>
      </div>

      <Tabs defaultValue="invoices">
        <TabsList>
          <TabsTrigger value="invoices" className="gap-1"><FileText size={14} /> Fatture ({invoices.length})</TabsTrigger>
          <TabsTrigger value="payments" className="gap-1"><CreditCard size={14} /> Pagamenti ({payments.length})</TabsTrigger>
          <TabsTrigger value="transactions" className="gap-1"><ArrowRightLeft size={14} /> Transazioni ({transactions.length})</TabsTrigger>
        </TabsList>

        {/* FATTURE */}
        <TabsContent value="invoices">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                  <Input placeholder="Cerca fattura..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
                </div>
                <Button onClick={() => { setEditing(null); setForm({ supplier_name: '', invoice_number: '', amount: '', invoice_date: new Date().toISOString().split('T')[0], due_date: '', description: '', notes: '' }); setShowDialog('invoice'); }}>
                  <Plus size={16} className="mr-1" /> Nuova Fattura
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>N. Fattura</TableHead><TableHead>Fornitore</TableHead><TableHead>Importo</TableHead>
                  <TableHead>Data</TableHead><TableHead>Scadenza</TableHead><TableHead>Stato</TableHead><TableHead className="w-[120px]">Azioni</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {invoices.filter(i => !search || i.supplier_name?.toLowerCase().includes(search.toLowerCase()) || i.invoice_number?.toLowerCase().includes(search.toLowerCase())).map(inv => (
                    <TableRow key={inv.id} className="hover:bg-slate-50">
                      <TableCell className="font-mono text-sm">{inv.invoice_number || '-'}</TableCell>
                      <TableCell className="font-semibold">{inv.supplier_name}</TableCell>
                      <TableCell className="font-semibold">{formatCurrency(inv.amount)}</TableCell>
                      <TableCell className="text-sm">{formatDate(inv.invoice_date)}</TableCell>
                      <TableCell className="text-sm">{formatDate(inv.due_date)}</TableCell>
                      <TableCell>{statusBadge(inv.status, inv.due_date)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {inv.status !== 'pagata' && <Button variant="ghost" size="sm" onClick={() => { setEditing(inv); setForm({}); setShowDialog('pay_invoice'); }} className="p-1 h-auto" title="Segna come pagata"><Check size={14} className="text-green-600" /></Button>}
                          <Button variant="ghost" size="sm" onClick={() => { setEditing(inv); setForm({...inv}); setShowDialog('invoice'); }} className="p-1 h-auto"><Pencil size={14} className="text-blue-600" /></Button>
                          {isRole('admin') && <Button variant="ghost" size="sm" onClick={() => handleDelete('invoice', inv.id)} className="p-1 h-auto"><Trash2 size={14} className="text-red-600" /></Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {invoices.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Nessuna fattura</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PAGAMENTI */}
        <TabsContent value="payments">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex justify-end">
                <Button onClick={() => { setEditing(null); setForm({ description: '', amount: '', due_date: '', notes: '' }); setShowDialog('payment'); }}>
                  <Plus size={16} className="mr-1" /> Nuovo Pagamento
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Descrizione</TableHead><TableHead>Importo</TableHead><TableHead>Scadenza</TableHead><TableHead>Stato</TableHead><TableHead className="w-[120px]">Azioni</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {payments.map(p => (
                    <TableRow key={p.id} className="hover:bg-slate-50">
                      <TableCell className="font-semibold">{p.description}</TableCell>
                      <TableCell className="font-semibold">{formatCurrency(p.amount)}</TableCell>
                      <TableCell className="text-sm">{formatDate(p.due_date)}</TableCell>
                      <TableCell>{statusBadge(p.status, p.due_date)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {p.status !== 'pagata' && <Button variant="ghost" size="sm" onClick={() => { setEditing(p); setForm({}); setShowDialog('pay_payment'); }} className="p-1 h-auto"><Check size={14} className="text-green-600" /></Button>}
                          <Button variant="ghost" size="sm" onClick={() => { setEditing(p); setForm({...p}); setShowDialog('payment'); }} className="p-1 h-auto"><Pencil size={14} className="text-blue-600" /></Button>
                          {isRole('admin') && <Button variant="ghost" size="sm" onClick={() => handleDelete('payment', p.id)} className="p-1 h-auto"><Trash2 size={14} className="text-red-600" /></Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {payments.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Nessun pagamento</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TRANSAZIONI */}
        <TabsContent value="transactions">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex justify-end">
                <Button onClick={() => { setEditing(null); setForm({ description: '', amount: '', payment_method: '', transaction_date: new Date().toISOString().split('T')[0], notes: '' }); setShowDialog('transaction'); }}>
                  <Plus size={16} className="mr-1" /> Nuova Transazione
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Descrizione</TableHead><TableHead>Importo</TableHead><TableHead>Metodo</TableHead><TableHead>Data</TableHead><TableHead className="w-[80px]">Azioni</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {transactions.map(t => (
                    <TableRow key={t.id} className="hover:bg-slate-50">
                      <TableCell className="font-semibold">{t.description}</TableCell>
                      <TableCell className="font-semibold">{formatCurrency(t.amount)}</TableCell>
                      <TableCell><Badge variant="outline">{t.payment_method || '-'}</Badge></TableCell>
                      <TableCell className="text-sm">{formatDate(t.transaction_date)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { setEditing(t); setForm({...t}); setShowDialog('transaction'); }} className="p-1 h-auto"><Pencil size={14} className="text-blue-600" /></Button>
                          {isRole('admin') && <Button variant="ghost" size="sm" onClick={() => handleDelete('transaction', t.id)} className="p-1 h-auto"><Trash2 size={14} className="text-red-600" /></Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {transactions.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Nessuna transazione</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Fattura */}
      <Dialog open={showDialog === 'invoice'} onOpenChange={(o) => !o && setShowDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Modifica Fattura' : 'Nuova Fattura'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>N. Fattura</Label><Input value={form.invoice_number || ''} onChange={(e) => setForm({...form, invoice_number: e.target.value})} /></div>
              <div><Label>Importo *</Label><Input type="number" step="0.01" value={form.amount || ''} onChange={(e) => setForm({...form, amount: e.target.value})} /></div>
            </div>
            <div><Label>Fornitore *</Label>
              <select className="flex h-9 w-full rounded-md border border-slate-200 bg-transparent px-3 py-1 text-sm shadow-sm" value={form.supplier_name || ''} onChange={(e) => setForm({...form, supplier_name: e.target.value})}>
                <option value="">Seleziona...</option>
                {suppliers.map(s => <option key={s.id} value={s.company_name}>{s.company_name}</option>)}
              </select>
            </div>
            <div><Label>Descrizione</Label><Input value={form.description || ''} onChange={(e) => setForm({...form, description: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data Fattura</Label><Input type="date" value={form.invoice_date || ''} onChange={(e) => setForm({...form, invoice_date: e.target.value})} /></div>
              <div><Label>Scadenza</Label><Input type="date" value={form.due_date || ''} onChange={(e) => setForm({...form, due_date: e.target.value})} /></div>
            </div>
            <div><Label>Note</Label><Textarea value={form.notes || ''} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(null)}>Annulla</Button>
            <Button onClick={() => handleSave('invoice')} disabled={!form.supplier_name || !form.amount}>{editing ? 'Aggiorna' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Pagamento */}
      <Dialog open={showDialog === 'payment'} onOpenChange={(o) => !o && setShowDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Modifica Pagamento' : 'Nuovo Pagamento'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Descrizione *</Label><Input value={form.description || ''} onChange={(e) => setForm({...form, description: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Importo *</Label><Input type="number" step="0.01" value={form.amount || ''} onChange={(e) => setForm({...form, amount: e.target.value})} /></div>
              <div><Label>Scadenza</Label><Input type="date" value={form.due_date || ''} onChange={(e) => setForm({...form, due_date: e.target.value})} /></div>
            </div>
            <div><Label>Note</Label><Textarea value={form.notes || ''} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(null)}>Annulla</Button>
            <Button onClick={() => handleSave('payment')} disabled={!form.description || !form.amount}>{editing ? 'Aggiorna' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Transazione */}
      <Dialog open={showDialog === 'transaction'} onOpenChange={(o) => !o && setShowDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Modifica Transazione' : 'Nuova Transazione'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Descrizione *</Label><Input value={form.description || ''} onChange={(e) => setForm({...form, description: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Importo *</Label><Input type="number" step="0.01" value={form.amount || ''} onChange={(e) => setForm({...form, amount: e.target.value})} /></div>
              <div><Label>Data</Label><Input type="date" value={form.transaction_date || ''} onChange={(e) => setForm({...form, transaction_date: e.target.value})} /></div>
            </div>
            <div><Label>Metodo Pagamento</Label>
              <Select value={form.payment_method || ''} onValueChange={(v) => setForm({...form, payment_method: v})}>
                <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="contanti">Contanti</SelectItem>
                  <SelectItem value="bonifico">Bonifico</SelectItem>
                  <SelectItem value="carta">Carta</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="assegno">Assegno</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Note</Label><Textarea value={form.notes || ''} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(null)}>Annulla</Button>
            <Button onClick={() => handleSave('transaction')} disabled={!form.description || !form.amount}>{editing ? 'Aggiorna' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Segna come Pagata */}
      <Dialog open={showDialog === 'pay_invoice' || showDialog === 'pay_payment'} onOpenChange={(o) => !o && setShowDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Segna come Pagata</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Importo: <strong>{formatCurrency(editing?.amount)}</strong></p>
            <div><Label>URL Contabile / Ricevuta</Label><Input value={form.payment_receipt_url || form.receipt_url || ''} onChange={(e) => setForm({...form, payment_receipt_url: e.target.value, receipt_url: e.target.value})} placeholder="Link al documento..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(null)}>Annulla</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={() => handleMarkPaid(showDialog === 'pay_invoice' ? 'invoice' : 'payment', editing)}>Conferma Pagamento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
